import {
  ActionConfirmationModal,
  Button,
  Form,
  FormSubmitButton,
  TextAreaFormField,
  TextFormField,
} from "@/components/ui";
import { AppModal } from "@/components/ui/AppModal/AppModal";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import { useUser } from "@/components/UserProvider";
import {
  useCreateMatterRole,
  useDeleteMatterRole,
  useUpdateMatterRole,
} from "@/lib/data/react-query/matters/matter-role";
import { retrieveMatterSettingsQueryOptions } from "@/lib/data/react-query/matters/matter-settings";
import { MatterRoleSchema } from "@/lib/data/schemas/matters/MatterSchema";

import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import { type MatterRole } from "@/lib/types";
import { slideoverFieldsetClassName } from "@/lib/utils/slideover-classes";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { toLower } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { type z } from "zod";

interface AddOrEditMatterRoleModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

type ConditionalProps =
  | {
      isReadOnly?: boolean;
      editing: true;
      activeMatterRole: Pick<MatterRole, "id" | "dateModified"> &
        Partial<MatterRole>;
      setActiveMatterRoleId: (id: string) => void;
    }
  | {
      isReadOnly?: never;
      editing: false;
      activeMatterRole?: never;
      setActiveMatterRoleId?: never;
    };

const formSchema = MatterRoleSchema.pick({
  label: true,
  description: true,
});

const defaultFormValues = {
  label: "",
  description: "",
} as const;

const FORM_ID = "matter-role-form";

export function AddOrEditMatterRoleModal({
  open,
  setOpen,
  activeMatterRole,
  editing,
  isReadOnly,
}: AddOrEditMatterRoleModalProps & ConditionalProps) {
  const { hasActionPermission } = useUser();
  const [isDeleting, setIsDeleting] = useState(false);
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    values: editing
      ? { ...defaultFormValues, ...activeMatterRole }
      : defaultFormValues,
    mode: "onSubmit",
  });

  const createMatterRole = useCreateMatterRole();
  const editMatterRole = useUpdateMatterRole();
  const deleteMatterRole = useDeleteMatterRole();
  const { data: matterSettingsData } = useQuery(
    retrieveMatterSettingsQueryOptions(),
  );
  const matterRoles = matterSettingsData?.data.roles ?? [];
  const isFormLoading =
    createMatterRole.isPending ||
    editMatterRole.isPending ||
    deleteMatterRole.isPending;

  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    if (
      matterRoles.some(
        (matterRole) =>
          toLower(matterRole.label.trim()) === toLower(data.label.trim()) &&
          matterRole.id !== activeMatterRole?.id,
      )
    ) {
      form.setError("label", {
        message: "Matter role label must be unique",
      });
      return;
    }
    setOpen(false);
    if (editing) {
      await editMatterRole.mutateAsync({
        id: activeMatterRole.id,
        dateModified: activeMatterRole.dateModified,
        ...data,
      });
    } else {
      await createMatterRole.mutateAsync({
        ...data,
      });
    }
    form.reset();
  };

  const handleDelete = async () => {
    setOpen(false);
    if (editing) {
      await deleteMatterRole
        .mutateAsync({
          id: activeMatterRole.id,
        })
        .then(() => {
          setIsDeleting(false);
        });
    }
  };

  const sheetTitle = useMemo(
    () =>
      isReadOnly
        ? "View assignee role"
        : editing
          ? "Update assignee role"
          : "Create assignee role",
    [isReadOnly, editing],
  );

  const sheetDescription = useMemo(
    () =>
      isReadOnly
        ? "View the assignee role details below"
        : editing
          ? "Update the assignee role details below"
          : "Create a new assignee role to your firm",
    [isReadOnly, editing],
  );

  useEffect(() => {
    if (!open) {
      form.reset();
    }
  }, [open, form]);

  const canDelete =
    !isReadOnly &&
    editing &&
    hasActionPermission(
      ACTION_PERMISSION_KEYS.matterRole,
      ACTION_PERMISSION_VALUES.delete,
    );

  const deleteButton = canDelete ? (
    <div>
      <Button
        type="button"
        variant="destructive"
        onClick={() => {
          setIsDeleting(true);
        }}
      >
        Delete
      </Button>
    </div>
  ) : null;

  return (
    <>
      <Form {...form}>
        <AppModal
          open={open}
          onOpenChange={setOpen}
          title={sheetTitle}
          description={sheetDescription}
          isDirty={form.formState.isDirty}
          loading={isFormLoading}
          size="xl"
          footer={
            <div className="flex w-full flex-wrap items-center justify-between gap-2">
              {deleteButton}
              <div className="flex grow justify-end space-x-2">
                <FormCancelButton
                  label={isReadOnly ? "Close" : "Cancel"}
                  setOpen={setOpen}
                  isDirty={form.formState.isDirty}
                />
                {!isReadOnly && <FormSubmitButton submitFormId={FORM_ID} />}
              </div>
            </div>
          }
        >
          <form
            id={FORM_ID}
            className="flex min-h-0 grow flex-col overflow-hidden"
            onSubmit={form.handleSubmit(onSubmit)}
          >
            <div className="overflow-y-auto">
              <fieldset
                disabled={isReadOnly}
                className={slideoverFieldsetClassName}
              >
                <TextFormField
                  register={form.register("label")}
                  label="Label*"
                />
                <div className="col-span-2">
                  <TextAreaFormField
                    register={form.register("description")}
                    label="Description"
                  />
                </div>
              </fieldset>
            </div>
          </form>
        </AppModal>
      </Form>

      <ActionConfirmationModal
        title={`Are you sure you want to delete this matter role?`}
        open={isDeleting && !!activeMatterRole}
        setOpen={setIsDeleting}
        afterActionFn={async () => {
          if (activeMatterRole) {
            await handleDelete();
          }
        }}
      />
    </>
  );
}
